import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { ICategoryDataSummaryModel } from "../../types/types";
import { useSelector } from "react-redux";
import { RootState } from "../../store/reducers";
import useMediaQuery from "@mui/material/useMediaQuery";
import theme from "../../styles/theme";
import { getColorForId } from "../../utils/chartColors";

interface BarChartWrapperProps {
  category:
    | "type"
    | "source"
    | "reason"
    | "coAgent"
    | "specialGroups"
    | "specialTags"
    | "zipCode"
    | "county"
    | "customArea"
    | "vendors";
  yearIds?: number[];
  mode: "count" | "amount" | "average";
  onSliceClick?: (variableId: number, value: string, color: string) => void;
  yearText?: string | number;
}

export const BarChartWrapper: React.FC<BarChartWrapperProps> = ({
  category,
  yearIds,
  mode,
  onSliceClick,
  yearText,
}) => {
  const { categories } = useSelector((state: RootState) => state.analytics);
  const categoryData = categories[category]?.data;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const data = categoryData
    ?.reduce((acc, item) => {
      const existing = acc.find((i) => i.id === item.id);
      if (existing) {
        existing.totalCount =
          (existing.totalCount || 0) + (item.totalCount || 0);
        existing.totalAmount =
          (existing.totalAmount || 0) + (item.totalAmount || 0);
      } else {
        acc.push({
          ...item,
          totalCount: item.totalCount || 0,
          totalAmount: item.totalAmount || 0,
        });
      }
      return acc;
    }, [] as ICategoryDataSummaryModel[])
    ?.map((item) => ({
      name: item.name,
      id: item.id,
      value:
        mode === "count"
          ? item.totalCount || 0
          : mode === "amount"
            ? item.totalAmount || 0
            : (item.totalCount || 0) > 0
              ? (item.totalAmount || 0) / (item.totalCount || 1)
              : 0,
    }))
    ?.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );

  const handleBarClick = (data: any, index: number) => {
    if (onSliceClick && data.id) {
      onSliceClick(data.id, data.name, getColorForId(data.id));
    }
  };

  return (
    <>
      {yearText && (
        <div
          style={{
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "16px",
            color: "#1976d2",
            padding: "5px",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            marginBottom: "10px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          Year: {yearText}
        </div>
      )}
      <ResponsiveContainer width="100%" height={450}>
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis
            dataKey="name"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            tick={{ fontSize: isMobile ? 10 : 12, fill: "#666" }}
          />
          <YAxis
            tick={{ fontSize: isMobile ? 10 : 12, fill: "#666" }}
            tickFormatter={(value) =>
              mode === "amount" || mode === "average"
                ? new Intl.NumberFormat("en-US", {
                    notation: "compact",
                    compactDisplay: "short",
                  }).format(value)
                : value.toString()
            }
          />
          <Tooltip
            formatter={(value) => [
              mode === "amount" || mode === "average"
                ? new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                  }).format(Number(value))
                : value,
              mode === "count"
                ? "Units"
                : mode === "amount"
                  ? "Volume"
                  : "Average Price",
            ]}
            contentStyle={{
              backgroundColor: "white",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          />
          <Bar
            dataKey="value"
            onClick={handleBarClick}
            cursor="pointer"
            radius={[4, 4, 0, 0]}
          >
            {data?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColorForId(entry.id || index)}
                style={{ transition: "all 0.3s ease" }}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};
